import { Button } from '@/components/ui/Button'
import { formatCurrency } from '@/utils/format-currency'
import { cn } from '@/utils/cn'
import { PARTNER_COMMISSION_MODE_LABEL } from '@/components/Partners/Modals/partnerDetailModalConstants'
import type { Partner } from '@/types'

interface PartnerDetailDealsTabProps {
  partner: Partner
  onCalculateCommission?: (partnerId: string, dealId: string) => void
  onRecurrenceAction?: (dealId: string, action: 'end' | 'reopen') => void
}

export function PartnerDetailDealsTab({ partner, onCalculateCommission, onRecurrenceAction }: PartnerDetailDealsTabProps) {
  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Empresa / Deal', 'Status', 'Valor', 'Comissão'].map((h) => (
                <th key={h} className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(partner.deals ?? []).map((d) => {
              const ownCommissions = d.comissoes.filter((c) => !c.parceiroId || c.parceiroId === partner.id)
              const isWon = String(d.etapa).toUpperCase() === 'GANHO'
              const isRecurring = d.modoComissaoParceiro === 'RECORRENTE'
              const recurrenceEnded = Boolean(d.recorrenciaParceiroEncerradaEm)

              return (
                <tr key={d.id} className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors'>
                  <td className='px-4 py-3'>
                    <p className='font-medium text-[var(--font-color-body)] text-sm'>{d.empresa?.nome ?? '—'}</p>
                    <p className='text-[var(--font-color-muted)] text-[11px]'>{d.nome}</p>
                  </td>
                  <td className='px-4 py-3'>
                    <span className={cn(
                      'px-2 py-0.5 rounded-md text-[9px] font-black uppercase',
                      isWon ? 'bg-emerald-500/10 text-emerald-500' : String(d.etapa).toUpperCase() === 'PERDIDO' ? 'bg-rose-500/10 text-rose-500' : 'bg-[var(--glass-bg)] text-[var(--text-muted)]'
                    )}>
                      {d.etapa}
                    </span>
                    {d.modoComissaoParceiro && (
                      <p className='mt-1 text-[10px] text-[var(--font-color-muted)]'>
                        {PARTNER_COMMISSION_MODE_LABEL[d.modoComissaoParceiro]}
                        {isRecurring && recurrenceEnded && ' · encerrada'}
                      </p>
                    )}
                  </td>
                  <td className='px-4 py-3 text-sm font-bold text-[var(--font-color-body)]'>
                    {formatCurrency(d.valor)}
                  </td>
                  <td className='px-4 py-3'>
                    <div className='flex flex-col items-start gap-1'>
                      {ownCommissions.length > 0 ? (
                        <span className='text-sm font-black text-emerald-500'>
                          {formatCurrency(ownCommissions.reduce((acc, c) => acc + Number(c.valor), 0))}
                        </span>
                      ) : isWon ? (
                        <Button variant='ghost' size='sm' onClick={() => onCalculateCommission?.(partner.id, d.id)}>
                          Calcular
                        </Button>
                      ) : (
                        <span className='text-[var(--font-color-muted)] text-sm'>—</span>
                      )}
                      {isWon && isRecurring && onRecurrenceAction && (
                        <button
                          type='button'
                          onClick={() => onRecurrenceAction(d.id, recurrenceEnded ? 'reopen' : 'end')}
                          className={cn(
                            'text-[11px] font-medium hover:underline',
                            recurrenceEnded ? 'text-[var(--brand-primary)]' : 'text-rose-500',
                          )}
                        >
                          {recurrenceEnded ? 'Reabrir recorrência' : 'Encerrar recorrência'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {(!partner.deals || partner.deals.length === 0) && (
              <tr>
                <td colSpan={4} className='px-4 py-8 text-center text-[var(--font-color-muted)] text-sm'>
                  Nenhuma negociação encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
